import { ApolloProvider } from '@apollo/client';
import { UiContextProvider, useUiContext } from '@pi/ui';
import React, { useEffect } from 'react';
import { Toaster } from 'sonner';

import { AppContextProvider, useAppContext } from './components/AppContext';
import AppRouter from './components/AppRouter';
import { Auth0Provider } from './components/Auth0/Auth0Provider';
import apolloClient from './startup/apollo';
import LS from './utils/LS';
import { setJwt, setupAuth0Session } from './utils/jwt';
import { isAuth0Session } from './utils/tokenProvider';

const AppInner: React.FC = () => {
    const { darkMode } = useAppContext();
    const { update: updateUiContext } = useUiContext();

    useEffect(() => updateUiContext({ darkMode }), [darkMode, updateUiContext]);

    if (isAuth0Session()) {
        // No PI token to rehydrate - the api client is wired to ask the SDK per request instead.
        setupAuth0Session();
    } else {
        setJwt(LS.get('jwt') || '');
    }

    return (
        <div className={darkMode ? 'bp4-dark dark' : ''}>
            <AppRouter />
        </div>
    );
};

/**
 * Mounts the Auth0 SDK only where it is configured.
 *
 * Deliberately synchronous - this sits above the router, so an async answer would delay first paint
 * for every user, including the ones who never touch Auth0. Whether a given *user* may sign in is a
 * separate question, answered by the `access` permission on the Auth0 API and enforced in api-v2.
 */
const AppAuthProviders: React.FC = () => (
    <Auth0Provider>
        <AppInner />
    </Auth0Provider>
);

const App: React.FC = () => (
    <ApolloProvider client={apolloClient}>
        <AppContextProvider>
            <UiContextProvider>
                <AppAuthProviders />
                <Toaster position='top-right' />
            </UiContextProvider>
        </AppContextProvider>
    </ApolloProvider>
);
export default App;
